import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Wind, Users, AlertTriangle, TrendingUp, TrendingDown,
  Plus, ChevronRight, Activity, Loader2, Calendar, Filter,
} from 'lucide-react';

const TEST_LABELS = { YOYO: 'Yo-Yo Test', GACON: 'Gacon Test' };

const ALERT_STYLES = {
  PERFORMANCE_DROP: { color: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/30' },
  BELOW_TEAM_AVG: { color: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/30' },
  MISSING_TEST: { color: 'text-zinc-400', bg: 'bg-zinc-500/10', border: 'border-zinc-500/30' },
};

export default function StaffAerobicPage() {
  const { api } = useAuth();
  const { language } = useLanguage();
  const t = (en, it) => language === 'IT' ? it : en;

  const [sessions, setSessions] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterTeam, setFilterTeam] = useState('all');
  const [filterType, setFilterType] = useState('all');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterTeam !== 'all') params.team_id = filterTeam;
      if (filterType !== 'all') params.test_type = filterType;
      const [sessR, teamsR] = await Promise.all([
        api.get('/aerobic/sessions', { params }),
        api.get('/teams'),
      ]);
      setSessions(sessR.data);
      setTeams(teamsR.data);
      const teamId = filterTeam !== 'all' ? filterTeam : teamsR.data[0]?.id;
      if (teamId) {
        const alertsR = await api.get(`/aerobic/team/${teamId}/alerts`);
        setAlerts(alertsR.data);
      }
    } catch { /* ignore */ }
    setLoading(false);
  }, [api, filterTeam, filterType]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const completed = sessions.filter(s => s.status === 'COMPLETED');
  const latest = completed[0];

  if (loading) return <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-zinc-500" /></div>;

  return (
    <div className="space-y-6 fade-in" data-testid="staff-aerobic-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-zinc-100 flex items-center gap-2">
            <Wind className="w-6 h-6 text-teal-400" />
            {t('Aerobic Testing', 'Test Aerobici')}
          </h1>
          <p className="text-sm text-zinc-500 mt-1">{t('Manage aerobic performance testing', 'Gestisci i test aerobici')}</p>
        </div>
        <Link to="/staff/aerobic/group-test">
          <Button className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="new-group-test-btn">
            <Plus className="w-4 h-4 mr-2" /> {t('Group Test', 'Test di Gruppo')}
          </Button>
        </Link>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-zinc-900/50 border-zinc-800"><CardContent className="p-4 text-center">
          <Calendar className="w-5 h-5 text-teal-400 mx-auto mb-1" />
          <div className="text-2xl font-bold text-zinc-100" data-testid="stat-sessions">{sessions.length}</div>
          <div className="text-xs text-zinc-500 uppercase">{t('Sessions', 'Sessioni')}</div>
        </CardContent></Card>
        <Card className="bg-zinc-900/50 border-zinc-800"><CardContent className="p-4 text-center">
          <Users className="w-5 h-5 text-blue-400 mx-auto mb-1" />
          <div className="text-2xl font-bold text-zinc-100" data-testid="stat-athletes">{latest?.athletes_tested || 0}</div>
          <div className="text-xs text-zinc-500 uppercase">{t('Last Test Athletes', 'Atleti Ultimo Test')}</div>
        </CardContent></Card>
        <Card className="bg-zinc-900/50 border-zinc-800"><CardContent className="p-4 text-center">
          <TrendingUp className="w-5 h-5 text-emerald-400 mx-auto mb-1" />
          <div className="text-2xl font-bold text-zinc-100" data-testid="stat-best-mas">{latest?.summary?.best_mas || '—'}</div>
          <div className="text-xs text-zinc-500 uppercase">{t('Best MAS (km/h)', 'MAS Migliore')}</div>
        </CardContent></Card>
        <Card className="bg-zinc-900/50 border-zinc-800"><CardContent className="p-4 text-center">
          <AlertTriangle className="w-5 h-5 text-red-400 mx-auto mb-1" />
          <div className="text-2xl font-bold text-zinc-100" data-testid="stat-alerts">{alerts.length}</div>
          <div className="text-xs text-zinc-500 uppercase">{t('Alerts', 'Allerte')}</div>
        </CardContent></Card>
      </div>

      {/* Alerts */}
      {alerts.length > 0 && (
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader className="pb-3">
            <CardTitle className="text-zinc-100 text-base flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-red-400" /> {t('Alerts', 'Allerte')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {alerts.slice(0, 8).map((a, i) => {
              const st = ALERT_STYLES[a.type] || ALERT_STYLES.MISSING_TEST;
              return (
                <div key={i} className={`flex items-center justify-between p-3 rounded-sm border ${st.bg} ${st.border}`} data-testid={`alert-${i}`}>
                  <div className="flex items-center gap-2">
                    <AlertTriangle className={`w-4 h-4 ${st.color}`} />
                    <span className="text-sm text-zinc-200">{a.athlete_name}</span>
                    <span className={`text-xs ${st.color}`}>
                      {a.type === 'PERFORMANCE_DROP' && `MAS ${t('drop','calo')} ${a.drop_pct}%`}
                      {a.type === 'BELOW_TEAM_AVG' && `${a.below_pct}% ${t('below avg','sotto media')}`}
                      {a.type === 'MISSING_TEST' && t('No recent test', 'Nessun test recente')}
                    </span>
                  </div>
                  {a.test_type && <span className="text-xs text-zinc-500">{TEST_LABELS[a.test_type] || a.test_type}</span>}
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <div className="flex items-center gap-3">
        <Filter className="w-4 h-4 text-zinc-500" />
        <Select value={filterTeam} onValueChange={setFilterTeam}>
          <SelectTrigger className="w-40 bg-zinc-900 border-zinc-700 text-zinc-200" data-testid="filter-team">
            <SelectValue placeholder={t('All Teams', 'Tutti')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('All Teams', 'Tutti')}</SelectItem>
            {teams.map(tm => <SelectItem key={tm.id} value={tm.id}>{tm.code}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-44 bg-zinc-900 border-zinc-700 text-zinc-200" data-testid="filter-type">
            <SelectValue placeholder={t('All Types', 'Tutti')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('All Types', 'Tutti')}</SelectItem>
            {Object.entries(TEST_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Session List */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader className="pb-3">
          <CardTitle className="text-zinc-100 text-base">{t('Sessions', 'Sessioni')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {sessions.length === 0 ? (
            <div className="text-center py-8 text-zinc-500">{t('No sessions found', 'Nessuna sessione')}</div>
          ) : sessions.map(s => (
            <Link key={s.id} to={`/staff/aerobic/session/${s.id}`}
              className="flex items-center justify-between p-3 bg-zinc-950/50 rounded-sm border border-zinc-800 hover:border-zinc-700 transition-colors"
              data-testid={`session-row-${s.id}`}>
              <div className="flex items-center gap-3">
                <div className={`p-1.5 rounded-sm ${s.status === 'COMPLETED' ? 'bg-emerald-500/10' : 'bg-amber-500/10'}`}>
                  <Activity className={`w-4 h-4 ${s.status === 'COMPLETED' ? 'text-emerald-400' : 'text-amber-400'}`} />
                </div>
                <div>
                  <div className="font-medium text-zinc-100 text-sm">
                    {TEST_LABELS[s.test_type] || s.test_type}
                    {s.protocol_name && <span className="text-teal-400 ml-2 text-xs">{s.protocol_name}</span>}
                    {s.team_code && <span className="text-zinc-500 ml-2 text-xs">{s.team_code}</span>}
                  </div>
                  <div className="text-xs text-zinc-500 mt-0.5">
                    {s.date} • {s.athletes_tested} {t('athletes', 'atleti')}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {s.summary?.team_avg_mas != null && (
                  <span className="text-xs text-zinc-400">MAS: <span className="text-teal-400 font-medium">{s.summary.team_avg_mas} km/h</span></span>
                )}
                <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
                  s.status === 'COMPLETED' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'
                }`}>{s.status}</span>
                <ChevronRight className="w-4 h-4 text-zinc-600" />
              </div>
            </Link>
          ))}
        </CardContent>
      </Card>

      {/* Latest Summary Rankings */}
      {latest?.summary?.athlete_summaries && (
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader className="pb-3">
            <CardTitle className="text-zinc-100 text-base">
              {t('Latest Session Rankings', 'Classifica Ultimo Test')} — {TEST_LABELS[latest.test_type]} {latest.protocol_name || ''}
            </CardTitle>
            <CardDescription className="text-zinc-500">{latest.date} • {latest.team_code}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
              {latest.summary.team_avg_mas != null && (
                <div className="text-center p-3 bg-zinc-950/50 rounded-sm border border-zinc-800">
                  <div className="text-lg font-bold text-teal-400">{latest.summary.team_avg_mas} km/h</div>
                  <div className="text-xs text-zinc-500">{t('Team Avg MAS', 'MAS Media')}</div>
                </div>
              )}
              {latest.summary.best_mas != null && (
                <div className="text-center p-3 bg-emerald-500/5 rounded-sm border border-emerald-500/20">
                  <TrendingUp className="w-4 h-4 text-emerald-400 mx-auto mb-1" />
                  <div className="text-lg font-bold text-emerald-400">{latest.summary.best_mas} km/h</div>
                  <div className="text-xs text-zinc-500">{t('Best MAS', 'MAS Migliore')}</div>
                </div>
              )}
              {latest.summary.lowest_mas != null && (
                <div className="text-center p-3 bg-red-500/5 rounded-sm border border-red-500/20">
                  <TrendingDown className="w-4 h-4 text-red-400 mx-auto mb-1" />
                  <div className="text-lg font-bold text-red-400">{latest.summary.lowest_mas} km/h</div>
                  <div className="text-xs text-zinc-500">{t('Lowest MAS', 'MAS Minore')}</div>
                </div>
              )}
            </div>
            <div className="space-y-1.5">
              {Object.values(latest.summary.athlete_summaries)
                .sort((a, b) => (b.mas_vam_kmh || 0) - (a.mas_vam_kmh || 0))
                .map((as, i) => (
                  <div key={as.athlete_id} className="flex items-center justify-between p-2 rounded-sm hover:bg-zinc-800/30" data-testid={`ranking-${i}`}>
                    <div className="flex items-center gap-2">
                      <span className={`w-5 h-5 flex items-center justify-center rounded-full text-xs font-bold ${
                        i === 0 ? 'bg-amber-500/20 text-amber-400' : 'bg-zinc-800 text-zinc-500'
                      }`}>{i + 1}</span>
                      <span className="text-sm text-zinc-200">{as.athlete_name || as.athlete_id?.slice(0, 8)}</span>
                    </div>
                    <div className="flex items-center gap-4 text-sm">
                      <span className="text-teal-400 font-semibold">{as.mas_vam_kmh} km/h</span>
                      {as.total_distance_m && <span className="text-zinc-400 text-xs">{as.total_distance_m}m</span>}
                      {as.heart_rate_bpm && <span className="text-zinc-500 text-xs">{as.heart_rate_bpm} bpm</span>}
                    </div>
                  </div>
                ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
